import Decimal from 'decimal.js';
import { priceApiBaseFrankfurter } from './priceApiBases';

const cache = new Map<string, Decimal>();
/** Requested UTC calendar day → ECB rate date actually used (weekend/holiday walk-back). */
const resolvedUsdEurDay = new Map<string, string>();

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export type UsdToEurResolved = {
  /** 1 USD = rate EUR (ECB via Frankfurter). */
  rate: Decimal;
  /** YYYY-MM-DD of the ECB rate used (may precede `date` on weekends/holidays). */
  rateDateIso: string;
};

/**
 * ECB-based daily rate: 1 USD = ? EUR (Frankfurter). Walks back up to 14 UTC days if a day has no rate (weekends/holidays).
 * Also returns which calendar day’s rate was used (for exports / audit).
 */
async function resolveUsdToEurForDate(date: Date): Promise<UsdToEurResolved> {
  const key = isoDate(date);
  const hit = cache.get(key);
  if (hit != null) {
    const rateDateIso = resolvedUsdEurDay.get(key) ?? key;
    return { rate: hit, rateDateIso };
  }

  let d = new Date(date.getTime());
  for (let back = 0; back < 14; back++) {
    const k = isoDate(d);
    const cached = cache.get(k);
    if (cached != null) {
      resolvedUsdEurDay.set(key, k);
      cache.set(key, cached);
      return { rate: cached, rateDateIso: k };
    }

    const u = `${priceApiBaseFrankfurter()}/${k}?from=USD&to=EUR`;
    let res: Response;
    try {
      res = await fetch(u);
    } catch {
      throw new Error(
        'Frankfurter USD→EUR fetch failed (network). Check connectivity to https://api.frankfurter.dev'
      );
    }
    if (res.ok) {
      const data = (await res.json()) as { rates?: { EUR?: number } };
      const rate = data.rates?.EUR;
      if (rate != null) {
        const dec = new Decimal(rate);
        cache.set(k, dec);
        resolvedUsdEurDay.set(key, k);
        if (back > 0) cache.set(key, dec);
        return { rate: dec, rateDateIso: k };
      }
    }
    d.setUTCDate(d.getUTCDate() - 1);
  }
  throw new Error(`Frankfurter: no USD→EUR rate within 14 days before ${isoDate(date)}`);
}

/** ECB-based daily rate: 1 USD = ? EUR (Frankfurter). Walks back up to 14 UTC days if a day has no rate (weekends/holidays). */
export async function usdToEurOnDate(date: Date): Promise<Decimal> {
  return (await resolveUsdToEurForDate(date)).rate;
}

/** Same as `usdToEurOnDate` plus the ECB reference date used after weekend/holiday walk-back. */
export async function usdToEurOnDateWithResolvedDay(date: Date): Promise<UsdToEurResolved> {
  return resolveUsdToEurForDate(date);
}

export async function convertUsdToEur(usd: Decimal, date: Date): Promise<Decimal> {
  const r = await usdToEurOnDate(date);
  return usd.mul(r);
}

const gbpEurRateCache = new Map<string, Decimal>();

/** ECB-based: 1 GBP = ? EUR (Frankfurter). Same weekend walk-back as USD→EUR. */
export async function gbpToEurOnDate(date: Date): Promise<Decimal> {
  const key = isoDate(date);
  const hit = gbpEurRateCache.get(key);
  if (hit) return hit;

  let d = new Date(date.getTime());
  for (let back = 0; back < 14; back++) {
    const k = isoDate(d);
    const cached = gbpEurRateCache.get(k);
    if (cached) {
      if (back > 0) gbpEurRateCache.set(key, cached);
      return cached;
    }

    const u = `${priceApiBaseFrankfurter()}/${k}?from=GBP&to=EUR`;
    let res: Response;
    try {
      res = await fetch(u);
    } catch {
      throw new Error(
        'Frankfurter GBP→EUR fetch failed (network). Check connectivity to https://api.frankfurter.dev'
      );
    }
    if (res.ok) {
      const data = (await res.json()) as { rates?: { EUR?: number } };
      const rate = data.rates?.EUR;
      if (rate != null) {
        const dec = new Decimal(rate);
        gbpEurRateCache.set(k, dec);
        if (back > 0) gbpEurRateCache.set(key, dec);
        return dec;
      }
    }
    d.setUTCDate(d.getUTCDate() - 1);
  }
  throw new Error(`Frankfurter: no GBP→EUR rate within 14 days before ${isoDate(date)}`);
}

export async function convertGbpToEur(gbp: Decimal, date: Date): Promise<Decimal> {
  const r = await gbpToEurOnDate(date);
  return gbp.mul(r);
}
